import React, { useMemo, useState } from "react";
import { RadioButton } from "primereact/radiobutton";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { Message } from "primereact/message";
import { formatNumberCurrency } from "./Utils";
import { agruparTrechos, validarSelecao, LACUNA_ROTEIRO_MAX_NOITES } from "../Constants/quote";
import { publicBaseUrl } from "../Utils/publicUrl";
import imagePlaceHolder from "../Assets/Images/noimg.jpg";

/**
 * QuoteSelectionView — seleção + aprovação de uma cotação (Fase 2).
 * COMPARTILHADO: usado na página pública (/cotacao/:token) e pela agência
 * ("operar para o cliente" via SUDO). Uma opção por bloco; TODO bloco é opcional
 * (aprovação parcial); só exige ao menos uma seleção. Buraco no roteiro só avisa.
 *
 * @param {object}  quote      cotação (shape mock/normalizado, com `trechos` e `itens`)
 * @param {object}  selecao    { [trechoId]: itemId | null } controlado (opcional)
 * @param {func}    onSelecao  callback(novaSelecao)
 * @param {func}    onAprovar  callback(selecao, { parcial, naoSelecionados }) — só quando válido.
 *                             O chamador usa `parcial` para confirmar o descarte dos blocos de fora.
 * @param {boolean} aprovando  loading do botão
 * @param {boolean} readOnly   só leitura (sem aprovar) — ex.: status já aprovado
 * @param {boolean} preview    vitrine: mostra TODAS as opções com os dados do hotel,
 *                             sem rádio/aprovar/disclaimer (agência revê o que vai enviar)
 * @param {boolean} historico  cotação MORTA (perdida/cancelada): não há re-cota, então
 *                             disponibilidade é informação velha — some o selo
 *                             "Indisponível" e o esmaecido. Os valores ficam como registro.
 * @param {boolean} mostrarDescartadas  em `readOnly`, revela também o que NÃO foi escolhido
 *                             (a cotação original), esmaecido e rotulado. Owner 2026-08-06:
 *                             numa venda ganha/perdida a agência precisa reler o que foi
 *                             oferecido, não só o que sobrou. Só no admin.
 * @param {object}  desfechoPorTrecho  { [trechoId]: { status, codigoReserva } } — desfecho da
 *                             reserva de cada bloco (paga/cancelada/pendente). Com mais de uma
 *                             reserva na mesma cotação, é o que diz QUAL foi ganha e qual não.
 * @param {string}  actorLabel "cliente" | "agência" (texto do disclaimer/botão)
 */
const QuoteSelectionView = ({ quote, selecao: selecaoProp, onSelecao, onAprovar, aprovando, readOnly, preview, hideBanner, editableTrechos, onRemoveItem, historico, mostrarDescartadas, desfechoPorTrecho, actorLabel = "cliente" }) => {
	const trechos = useMemo(() => agruparTrechos(quote), [quote]);
	const [selecaoInterna, setSelecaoInterna] = useState(selecaoProp || quote?.selecaoCliente || {});
	const selecao = selecaoProp || selecaoInterna;
	// Colapsar cada bloco (local/noites) para otimizar a tela com muitas opções.
	const [colapsados, setColapsados] = useState(() => new Set());
	const toggleColapso = (id) => setColapsados((prev) => {
		const n = new Set(prev);
		n.has(id) ? n.delete(id) : n.add(id);
		return n;
	});

	const setSelecao = (nova) => {
		if (!selecaoProp) setSelecaoInterna(nova);
		onSelecao && onSelecao(nova);
	};
	const escolher = (trechoId, itemId) => setSelecao({ ...selecao, [trechoId]: itemId });
	const limpar = (trechoId) => { const n = { ...selecao }; delete n[trechoId]; setSelecao(n); };

	// Link público de detalhes do hotel (nova aba): /hotel/:token[?a=agencyViewToken].
	const hotelUrl = (op) => op?.hotelViewToken
		? `${publicBaseUrl()}/hotel/${op.hotelViewToken}${quote?.agencyViewToken ? `?a=${encodeURIComponent(quote.agencyViewToken)}` : ""}`
		: null;
	const abrirHotel = (op) => (e) => { e.preventDefault(); e.stopPropagation(); const u = hotelUrl(op); if (u) window.open(u, "_blank", "noopener"); };

	// Câmbio pode vir do BE como string ("5.42") — coage p/ número (evita usdBrl.toFixed crash).
	const usdBrl = Number(quote?.cambio?.usdBrl) || 0;
	// BRL de uma opção: usa o precoBRL do BE; se ausente, deriva de USD × câmbio.
	const brlDe = (op) => {
		const brl = Number(op?.precoBRL);
		if (Number.isFinite(brl) && brl > 0) return brl;
		return usdBrl ? Math.round((Number(op?.precoUSD) || 0) * usdBrl * 100) / 100 : (brl || 0);
	};
	const val = validarSelecao(trechos, selecao);
	// Vãos que valem uma nota na tela: curtos (lacuna de roteiro) e só durante a escolha.
	const lacunas = (readOnly || historico) ? [] : val.buracos.filter((b) => b.noites <= LACUNA_ROTEIRO_MAX_NOITES);
	const totalUSD = trechos.reduce((s, t) => {
		const it = t.opcoes.find((o) => o.id === selecao[t.id]);
		return s + (Number(it?.precoUSD) || 0);
	}, 0);
	const totalBRL = trechos.reduce((s, t) => {
		const it = t.opcoes.find((o) => o.id === selecao[t.id]);
		return s + (it ? brlDe(it) : 0);
	}, 0);
	// Com mais de uma reserva na mesma cotação, o câmbio deixa de ser um só: o item cuja reserva
	// foi paga travou na taxa daquele pagamento, o resto ainda acompanha o dia. Dizer "câmbio
	// R$ X" no total seria falso — e foi o que o owner viu na página pública (2026-08-08).
	const selecionadas = trechos.map((t) => t.opcoes.find((o) => o.id === selecao[t.id])).filter(Boolean);
	const qtdCongeladas = selecionadas.filter(congelado).length;
	const cambioMisto = qtdCongeladas > 0 && qtdCongeladas < selecionadas.length;
	const tudoCongelado = selecionadas.length > 0 && qtdCongeladas === selecionadas.length;

	return (
		<div className="quote-selection">
			{preview || (readOnly && hideBanner) ? null : readOnly ? (
				<Message
					severity="success"
					text="Esta foi a seleção aprovada. A agência já está preparando suas reservas."
					style={{ width: "100%", marginBottom: "1rem" }}
				/>
			) : (
				<Message
					severity="info"
					text={`Disponibilidade, preços e câmbio foram atualizados agora e valem apenas para este momento. Ao aprovar, a agência fará as reservas mantendo estes valores (podem ocorrer pequenas variações, ocasionalmente).`}
					style={{ width: "100%", marginBottom: "1rem" }}
				/>
			)}

			{trechos.map((t) => {
				const selecionadoId = selecao[t.id] || null;
				// Read-only (resumo aprovado): mostra só a opção escolhida; oculta trecho não incluído.
				// Com `mostrarDescartadas` o bloco inteiro reaparece — é a cotação ORIGINAL.
				if (readOnly && !selecionadoId && !mostrarDescartadas) return null;
				const opcoesRender = (readOnly && !mostrarDescartadas) ? t.opcoes.filter((o) => o.id === selecionadoId) : t.opcoes;
				// Desfecho da reserva deste bloco (item 5): identifica qual venda foi ganha/perdida.
				const desfecho = desfechoPorTrecho ? desfechoPorTrecho[t.id] : null;
				// Bloco que o cliente deixou de fora na aprovação (só visível com o toggle ligado).
				const blocoDescartado = readOnly && !selecionadoId;
				const colapsado = colapsados.has(t.id);
				// Nº de noites do trecho (evita mostrar o range de datas duas vezes no header).
				const noites = t.periodo?.noites
					?? (t.periodo?.checkIn && t.periodo?.checkOut
						? Math.max(0, Math.round((new Date(t.periodo.checkOut) - new Date(t.periodo.checkIn)) / 86400000))
						: null);
				return (
					<div key={t.id} className="quote-selection__trecho" style={trechoBox}>
						<div style={trechoHeader}>
							<div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
								<button type="button" aria-label={colapsado ? "Expandir" : "Colapsar"} onClick={() => toggleColapso(t.id)}
									style={chevronBtn}>
									<i className={`pi ${colapsado ? "pi-chevron-right" : "pi-chevron-down"}`} style={{ fontSize: "0.8rem" }} />
								</button>
								<div style={{ minWidth: 0 }}>
									<strong style={{ fontSize: "1rem" }}>{t.label}</strong>
									{noites !== null && noites !== undefined ? <span style={{ color: "#6B6B6B", marginLeft: 8 }}>{noites} noite(s)</span> : null}
									<span style={{ color: "#AAA", fontSize: "0.8rem", marginLeft: 8 }}>· {opcoesRender.length} opção(ões)</span>
								</div>
							</div>
							{/* Sem badge obrigatório/opcional: todo bloco é opcional (a regra virou
							    "ao menos uma seleção" — ver validarSelecao). O que entra aqui é o
							    DESFECHO da reserva do bloco: numa cotação com várias reservas, é o
							    único lugar que diz qual venda foi ganha e qual não. */}
							{desfecho ? (
								<Tag value={DESFECHO_TAG[desfecho.status]?.label || desfecho.status}
									severity={DESFECHO_TAG[desfecho.status]?.severity || "info"} />
							) : blocoDescartado ? (
								<Tag value="Não selecionado" severity="secondary" />
							) : null}
						</div>

						{!colapsado && opcoesRender.map((op) => {
							// Em histórico a disponibilidade não é reconsultada — não faz sentido
							// afirmar "indisponível" com base num dado congelado.
							const indisponivel = !historico && op.disponivel === false;
							const checked = selecionadoId === op.id;
							// Opção da cotação original que o cliente NÃO levou. Só existe com o
							// toggle ligado; fica esmaecida para não competir com a escolhida.
							const descartada = readOnly && mostrarDescartadas && !checked;
							// Prévia (agência): sem seleção/rádio → usa <div> para NÃO capturar
							// clique do label (que dispararia o botão de lixeira) e sem a borda
							// de "selecionado". Modo interativo (cliente): <label> + rádio.
							const OptEl = preview ? "div" : "label";
							return (
								<OptEl
									key={op.id}
									style={{ ...opcaoBox, ...(checked && !preview ? opcaoBoxSel : null), ...(indisponivel ? opcaoBoxOff : null), ...(preview ? opcaoBoxPreview : null), ...(descartada ? opcaoBoxDescartada : null) }}
								>
									{preview ? null : (
										<RadioButton
											checked={checked}
											disabled={indisponivel || readOnly}
											onChange={() => escolher(t.id, op.id)}
										/>
									)}
									<img src={op.hotel?.foto || imagePlaceHolder} alt="" style={opcaoImg}
										onError={(e) => { e.target.src = imagePlaceHolder; }} />
									<div style={{ flex: 1, minWidth: 0 }}>
										{/* Local em destaque ACIMA do nome do hotel — o cliente compara
										    opções por cidade antes de olhar a propriedade. */}
										{op.hotel?.cidade || op.hotel?.pais ? (
											<div style={localDestaque}>
												<i className="pi pi-map-marker" style={{ fontSize: "0.85rem", marginRight: 4 }} />
												{[op.hotel?.cidade, op.hotel?.pais].filter(Boolean).join(", ")}
											</div>
										) : null}
										<div style={{ fontWeight: 600 }}>{op.hotel?.nome}</div>
										<div style={{ color: "#6B6B6B", fontSize: "0.85rem" }}>
											{[op.quarto?.nome, op.quarto?.regime].filter(Boolean).join(" · ")}
										</div>
										{op.periodo?.checkIn ? (
											<div style={{ color: "#8A8A8A", fontSize: "0.8rem" }}>
												{op.periodo.checkIn.split("-").reverse().join("/")} → {op.periodo.checkOut.split("-").reverse().join("/")}
												{op.pax ? ` · ${op.pax.adultos || 0} adulto(s)${op.pax.criancas ? ` + ${op.pax.criancas} criança(s)` : ""}` : ""}
											</div>
										) : null}
										<div style={{ fontSize: "0.78rem", marginTop: 2, color: op.temCafe ? "#1e6b3a" : "#9aa0a6" }}>
											<i className={op.temCafe ? "pi pi-check" : "pi pi-times"} style={{ fontSize: "0.7rem", marginRight: 4 }} />
											{op.temCafe ? "Café da manhã incluído" : "Sem café da manhã"}
										</div>
										{hotelUrl(op) ? (
											<span role="button" tabIndex={0} onClick={abrirHotel(op)}
												onKeyDown={(e) => { if (e.key === "Enter") abrirHotel(op)(e); }}
												style={{ color: "#814DE5", fontSize: "0.8rem", cursor: "pointer", textDecoration: "underline", display: "inline-block", marginTop: 2 }}>
												Ver detalhes do hotel <i className="pi pi-external-link" style={{ fontSize: "0.7rem" }} />
											</span>
										) : null}
										{indisponivel ? <Tag value="Indisponível" severity="danger" style={{ marginTop: 4 }} /> : null}
										{descartada ? <Tag value="Não escolhida" severity="secondary" style={{ marginTop: 4 }} /> : null}
										{/* Localizador na opção escolhida: liga a linha da cotação à reserva
										    correspondente sem obrigar a descer até o bloco de Reservas. */}
										{checked && desfecho?.codigoReserva ? (
											<div style={{ color: "#8A8A8A", fontSize: "0.78rem", marginTop: 4 }}>
												localizador <strong>{desfecho.codigoReserva}</strong>
											</div>
										) : null}
									</div>
									<div style={{ textAlign: "right", whiteSpace: "nowrap" }}>
										<div style={{ fontWeight: 700, color: "#814DE5" }}>
											{formatNumberCurrency(brlDe(op), "BRL")}
										</div>
										<div style={{ color: "#6B6B6B", fontSize: "0.8rem" }}>
											USD {formatNumberCurrency(Number(op.precoUSD) || 0, "USD")}
										</div>
										{/* Reserva paga: o BRL parou na taxa daquele pagamento e não segue
										    mais o câmbio do dia (BE 2026-08-07). Dizer isso evita que o
										    valor parado — ao lado de outro que se mexe — pareça erro.
										    A taxa é o ideal; sem ela, o booleano ainda explica o porquê. */}
										{congelado(op) ? (
											<div style={{ color: "#2E9E5B", fontSize: "0.72rem", marginTop: 2 }}>
												<i className="pi pi-lock" style={{ fontSize: "0.66rem", marginRight: 3 }} />
												{op.cambioUsdBrlPago ? `congelado a R$ ${Number(op.cambioUsdBrlPago).toFixed(2)}` : "valor congelado"}
											</div>
										) : null}
									</div>
									{editableTrechos && onRemoveItem ? (
										<button type="button" aria-label="Remover opção" title="Remover opção"
											onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRemoveItem(op); }} style={removeBtn}>
											<i className="pi pi-trash" style={{ fontSize: "0.85rem" }} />
										</button>
									) : null}
								</OptEl>
							);
						})}

						{/* Todo bloco é opcional: desmarcar fica disponível sempre que houver seleção.
						    "Limpar seleção" e não "Não incluir essa opção" (owner 2026-08-07): o botão
						    fica embaixo da última opção do bloco e o rótulo antigo parecia se referir
						    a ELA, quando na verdade desmarca a escolha do bloco inteiro. */}
						{!colapsado && selecionadoId && !readOnly && !preview ? (
							<Button label="Limpar seleção" link onClick={() => limpar(t.id)} style={{ padding: "4px 0" }} />
						) : null}
					</div>
				);
			})}

			{preview ? null : (
			<>
			{/* Rodapé: total + validação */}
			<div style={rodape}>
				<div>
					{!val.algumSelecionado ? (
						<Message severity="warn" text="Selecione ao menos uma opção para aprovar." />
					) : lacunas.length > 0 ? (
						/* Nota de ROTEIRO, não erro: os blocos escolhidos deixam noites sem hotel.
						   Três filtros até chegar aqui (owner 2026-08-06/07):
						   - só ENQUANTO se escolhe (em leitura a escolha já foi feita e a frase
						     era lida como defeito da reserva);
						   - só vãos curtos — 3 semanas entre trechos são duas viagens, não lacuna;
						   - tom neutro e datas em DD/MM, no lugar do alerta amarelo com ISO cru. */
						<div style={{ color: "#6B6B6B", fontSize: "0.85rem" }}>
							<i className="pi pi-calendar" style={{ fontSize: "0.8rem", marginRight: 6 }} />
							{lacunas.map((b) => `${b.noites} noite(s) sem hospedagem entre ${fmtDiaCurto(b.de)} e ${fmtDiaCurto(b.ate)}`).join(" · ")}.
						</div>
					) : null}
				</div>
				<div style={{ textAlign: "right" }}>
					<div style={{ fontSize: "0.85rem", color: "#6B6B6B" }}>Total selecionado</div>
					<div style={{ fontSize: "1.4rem", fontWeight: 800, color: "#814DE5" }}>
						{formatNumberCurrency(totalBRL, "BRL")}
					</div>
					<div style={{ color: "#6B6B6B", fontSize: "0.85rem" }}>
						USD {formatNumberCurrency(totalUSD, "USD")}
						{/* Só afirma UM câmbio quando ele realmente vale para tudo. */}
						{!cambioMisto && !tudoCongelado && usdBrl ? ` · câmbio R$ ${usdBrl.toFixed(2)}` : ""}
					</div>
					{tudoCongelado ? (
						<div style={{ color: "#2E9E5B", fontSize: "0.78rem" }}>
							<i className="pi pi-lock" style={{ fontSize: "0.7rem", marginRight: 3 }} />
							valores congelados no pagamento
						</div>
					) : cambioMisto ? (
						<div style={{ color: "#6B6B6B", fontSize: "0.78rem", maxWidth: 320 }}>
							O que já foi pago está congelado na taxa do pagamento.
							{usdBrl ? ` O restante segue a cotação de câmbio vigente (R$ ${usdBrl.toFixed(2)}).` : ""}
						</div>
					) : null}
				</div>
			</div>

			{!readOnly ? (
				<Button
					label={actorLabel === "agência" ? "Aprovar para o cliente" : "Aprovar cotação"}
					icon="pi pi-check"
					disabled={!val.ok || aprovando}
					loading={aprovando}
					onClick={() => val.ok && onAprovar && onAprovar(selecao, { parcial: val.parcial, naoSelecionados: val.naoSelecionados })}
					style={{ width: "100%", marginTop: "1rem", background: "#814DE5", border: "none" }}
				/>
			) : null}
			</>
			)}
		</div>
	);
};

// Desfecho da reserva de um bloco — rótulos em linguagem de VENDA (owner 2026-08-05/06):
// numa cotação com várias reservas é isto que separa a ganha da perdida.
const DESFECHO_TAG = {
	paga:      { label: "Reserva paga", severity: "success" },
	cancelada: { label: "Reserva cancelada", severity: "danger" },
	pendente:  { label: "Aguardando pagamento", severity: "warning" },
};

// O valor deste item já travou? O BE manda a taxa (`cambioUsdBrlPago`) e, desde 2026-08-08, um
// booleano equivalente — aceitar os dois evita depender de qual deles chegou.
const congelado = (op) => !!(op?.cambioUsdBrlPago || op?.valorCongelado);

// YYYY-MM-DD -> DD/MM (avisos de roteiro; ano é ruído quando as datas são próximas).
const fmtDiaCurto = (d) => (d && /^\d{4}-\d{2}-\d{2}/.test(d) ? d.slice(5, 10).split("-").reverse().join("/") : (d || ""));

const trechoBox = { border: "1px solid #E5E5EA", borderRadius: 12, padding: 16, marginBottom: 16, background: "#fff" };
const trechoHeader = { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 };
const opcaoBox = { display: "flex", gap: 12, alignItems: "center", padding: 10, border: "1px solid #EEE", borderRadius: 10, marginBottom: 8, cursor: "pointer" };
const opcaoBoxSel = { border: "2px solid #814DE5", background: "#F7F3FF" };
const opcaoBoxOff = { opacity: 0.55, cursor: "not-allowed" };
// Opção da cotação original que não foi escolhida: presente, mas claramente secundária.
const opcaoBoxDescartada = { opacity: 0.5, borderStyle: "dashed", background: "#FBFBFC", cursor: "default" };
const opcaoBoxPreview = { cursor: "default" };
const chevronBtn = { background: "transparent", border: "none", cursor: "pointer", padding: 2, color: "#814DE5", display: "inline-flex", alignItems: "center", flexShrink: 0 };
const removeBtn = { background: "transparent", border: "none", cursor: "pointer", padding: 6, color: "#C0392B", display: "inline-flex", alignItems: "center", flexShrink: 0, alignSelf: "flex-start" };
const opcaoImg = { width: 157, height: 118, objectFit: "cover", borderRadius: 8, flexShrink: 0, alignSelf: "flex-start" };
// Local (cidade, país) em destaque acima do nome do hotel — roxo da marca.
const localDestaque = { fontSize: "1rem", marginBottom: "0.3rem", fontWeight: "bold", color: "var(--brand-primary)" };
const rodape = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, marginTop: 8, flexWrap: "wrap" };

export default QuoteSelectionView;
